import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Book, Award, Trophy, CheckCircle, GraduationCap, BookOpen, MessageSquare, ChevronRight, Sparkles, BookText, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import StudentGrades from './StudentGrades';
import StudentProfile from './StudentProfile';
import StudentLivret from './StudentLivret';
import { SubjectWithClass, Appreciation } from '../../types/supabase';

type SubjectAverage = {
  id: string;
  name: string;
  average: number;
  count: number;
  absences: number;
  coefficient: number;
  className?: string;
  teaching_type: string;
};

const StudentDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    className: ''
  });
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [subjectAverages, setSubjectAverages] = useState<SubjectAverage[]>([]);
  const [generalAverage, setGeneralAverage] = useState<number>(0);
  const [professionalAverage, setProfessionalAverage] = useState<number>(0);
  const [generalSubjectAverage, setGeneralSubjectAverage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [appreciations, setAppreciations] = useState<Appreciation[]>([]);
  const [subjectGrades, setSubjectGrades] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState<{start: string, end: string}>({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // January 1st of current year
    end: new Date().toISOString().split('T')[0] // Today
  });
  const [selectedPeriod, setSelectedPeriod] = useState<{start: string, end: string} | null>(null);
  const [periods, setPeriods] = useState<{start: string, end: string}[]>([]);

  // Fetch available periods from grades and appreciations
  useEffect(() => {
    if (user) {
      const fetchPeriods = async () => {
        try {
          // Get student ID first
          const { data: studentData } = await supabase
            .from('students')
            .select('id')
            .eq('email', user.email)
            .single();

          if (!studentData) return;

          // Get all unique dates from grades and appreciations
          const { data: gradesData } = await supabase
            .from('grades')
            .select('exam_date')
            .eq('student_id', studentData.id)
            .not('exam_date', 'is', null);

          const { data: appreciationsData } = await supabase
            .from('appreciations')
            .select('start_date, end_date')
            .eq('student_id', studentData.id);

          // Collect all dates
          const allDates = new Set<string>();
          
          gradesData?.forEach(grade => {
            if (grade.exam_date) allDates.add(grade.exam_date);
          });
          
          appreciationsData?.forEach(appreciation => {
            allDates.add(appreciation.start_date);
            allDates.add(appreciation.end_date);
          });

          // Sort dates
          const sortedDates = Array.from(allDates).sort();

          if (sortedDates.length > 0) {
            // Create periods from min/max dates
            const periods = [{
              start: sortedDates[0],
              end: sortedDates[sortedDates.length - 1]
            }];
            
            setPeriods(periods);
            setSelectedPeriod(periods[0]);
          }
        } catch (error) {
          console.error('Error fetching periods:', error);
        }
      };

      fetchPeriods();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      // Get student info including class
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select(`
          *,
          class:classes(name)
        `)
        .eq('email', user?.email)
        .single();

      if (studentError) throw studentError;

      setStats({
        className: studentData?.class?.name || ''
      });

      // Get grades and calculate averages
      const { data: gradesData, error: gradesError } = await supabase
        .from('grades')
        .select(`
          *,
          subject:subjects(
            id,
            name,
            coefficient,
            teaching_type,
            class:classes(name)
          )
        `)
        .eq('student_id', studentData.id);

      if (gradesError) throw gradesError;

      // Calculate subject averages
      const { start, end } = dateRange;
      const periodStart = selectedPeriod?.start;
      const periodEnd = selectedPeriod?.end;

      const subjectMap = new Map<string, SubjectAverage>();

      gradesData.forEach((grade) => {
        if (!grade.subject || grade.is_absent) return;
        if (grade.grade === null) return;
        
        // Filter by date range
        if (grade.exam_date) {
          if (grade.exam_date < start || grade.exam_date > end) return;
        }
        
        // Filter by period if selected
        if (periodStart && periodEnd && grade.exam_date) {
          if (grade.exam_date < periodStart || grade.exam_date > periodEnd) return;
        }

        const subjectId = grade.subject.id;
        const current = subjectMap.get(subjectId) || {
          id: subjectId,
          name: grade.subject.name,
          average: 0,
          count: 0,
          absences: 0,
          coefficient: grade.subject.coefficient || 1,
          teaching_type: grade.subject.teaching_type || 'enseignement_general',
          className: grade.subject.class?.name
        };

        current.average = (current.average * current.count + grade.grade) / (current.count + 1);
        current.count += 1;

        subjectMap.set(subjectId, current);
      });

      const averages = Array.from(subjectMap.values());
      setSubjectAverages(averages);

      // Calculate averages by teaching type
      const generalSubjects = averages.filter(s => s.teaching_type === 'enseignement_general');
      const professionalSubjects = averages.filter(s => s.teaching_type === 'enseignement_professionnel');

      // Only calculate averages if there are grades
      const hasGeneralGrades = generalSubjects.some(s => s.count > 0);
      const hasProfessionalGrades = professionalSubjects.some(s => s.count > 0);
      
      // General subjects average
      if (hasGeneralGrades) {
        const generalCoef = generalSubjects.reduce((sum, s) => s.count > 0 ? sum + s.coefficient : sum, 0);
        const generalSum = generalSubjects.reduce((sum, s) => s.count > 0 ? sum + s.average * s.coefficient : sum, 0);
        const generalAvg = generalCoef > 0 ? generalSum / generalCoef : null;
        setGeneralSubjectAverage(generalAvg !== null ? Number(generalAvg.toFixed(2)) : null);
      } else {
        setGeneralSubjectAverage(null);
      }
      
      // Professional subjects average
      if (hasProfessionalGrades) {
        const profCoef = professionalSubjects.reduce((sum, s) => s.count > 0 ? sum + s.coefficient : sum, 0);
        const profSum = professionalSubjects.reduce((sum, s) => s.count > 0 ? sum + s.average * s.coefficient : sum, 0);
        const profAvg = profCoef > 0 ? profSum / profCoef : null;
        setProfessionalAverage(profAvg !== null ? Number(profAvg.toFixed(2)) : null);
      } else {
        setProfessionalAverage(null);
      }
      
      // Overall average
      if (hasGeneralGrades || hasProfessionalGrades) {
        let totalAvg = 0;
        let divisor = 0;
        
        if (hasGeneralGrades) {
          totalAvg += generalSubjectAverage || 0;
          divisor++;
        }
        if (hasProfessionalGrades) {
          totalAvg += professionalAverage || 0;
          divisor++;
        }
        
        setGeneralAverage(divisor > 0 ? Number((totalAvg / divisor).toFixed(2)) : null);
      } else {
        setGeneralAverage(null);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching student stats:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchStats();
      fetchAppreciations();
    }
  }, [user, dateRange]);

  const fetchAppreciations = async () => {
    try {
      const { data, error } = await supabase
        .from('appreciations')
        .select(`
          *,
          subject:subjects(name),
          author:user_roles(nom, prenom)
        `);

      if (error) throw error;
      
      // Filter appreciations by period if selected
      let periodFilteredData = data;
      if (selectedPeriod) {
        periodFilteredData = data.filter(appreciation => 
          appreciation.start_date >= selectedPeriod.start &&
          appreciation.end_date <= selectedPeriod.end
        );
      }
      
      // Filter by date range
      const filteredData = data.filter(appreciation => 
        appreciation.start_date >= dateRange.start &&
        appreciation.end_date <= dateRange.end
      );
      
      // Apply both filters
      const finalData = selectedPeriod ? periodFilteredData : filteredData;
      setAppreciations(finalData);
    } catch (error) {
      console.error('Error fetching appreciations:', error);
    }
  };

  // Refresh data when period changes
  useEffect(() => {
    if (user) {
      fetchStats();
      fetchAppreciations();
    }
  }, [selectedPeriod]);

  const handleSubjectClick = async (subjectId: string) => {
    if (selectedSubject === subjectId) {
      setSelectedSubject(null);
      setSubjectGrades([]);
      return;
    }
    
    setSelectedSubject(subjectId);
    
    try {
      const { data: grades, error } = await supabase
        .from('grades')
        .select('*')
        .eq('subject_id', subjectId)
        .order('exam_date', { ascending: false });
        
      if (error) throw error;
      setSubjectGrades(grades);
    } catch (error) {
      console.error('Error fetching subject grades:', error);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getExamTypeText = (type: string | null) => {
    if (!type) return '-';
    
    const types: Record<string, string> = {
      'devoir_surveille': 'Devoir surveillé',
      'devoir_maison': 'Devoir maison',
      'participation': 'Participation',
      'examen_blanc': 'Examen blanc',
      'examen_officiel': 'Examen officiel'
    };
    
    return types[type] || type;
  };

  const DashboardHome = () => (
    <>
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Tableau de bord étudiant</h1>
        <p className="mt-2 text-gray-600">
          {stats.className && (
            <span>Classe: {stats.className}</span>
          )}
        </p>
      </header>

      {/* Date Range Filter */}
      <div className="mb-6">
        <div className="flex items-center space-x-4 bg-white rounded-lg shadow p-4">
          <Calendar className="h-5 w-5 text-gray-400" />
          <div className="flex items-center space-x-4 flex-1">
            <div>
              <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">
                Du
              </label>
              <input
                type="date"
                id="start-date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="input-field mt-1"
              />
            </div>
            <div>
              <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">
                au
              </label>
              <input
                type="date"
                id="end-date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="input-field mt-1"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold">Enseignement général</p>
              <p className="text-3xl font-bold mt-2">
                {isLoading ? '...' : generalSubjectAverage !== null ? generalSubjectAverage : '-'}
                <span className="text-lg opacity-75">/20</span>
              </p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-full">
              <BookText className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold">Enseignement professionnel</p>
              <p className="text-3xl font-bold mt-2">
                {isLoading ? '...' : professionalAverage !== null ? professionalAverage : '-'}
                <span className="text-lg opacity-75">/20</span>
              </p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-full">
              <Sparkles className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Professional Subjects */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Enseignement professionnel</h2>
        <div className="space-y-4">
          {subjectAverages
            .filter(subject => subject.teaching_type === 'enseignement_professionnel')
            .map((subject) => (
            <div key={subject.id}>
              <button
                onClick={() => handleSubjectClick(subject.id)}
                className="w-full bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 p-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">{subject.name}</h3>
                    {subject.className && (
                      <span className="text-sm text-gray-500">{subject.className}</span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className={`font-semibold text-lg ${
                      subject.average >= 10 ? 'text-success-500' : 'text-error-500'
                    }`}>{subject.count > 0 ? `${subject.average.toFixed(2)}/20` : '-'}</span>
                    <ChevronRight className={`h-5 w-5 ml-2 transition-transform ${
                      selectedSubject === subject.id ? 'rotate-90' : ''
                    }`} />
                  </div>
                </div>
                
                <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                  <span>Coefficient {subject.coefficient}</span>
                  <div className="flex items-center space-x-4">
                    <span>{subject.count} note{subject.count !== 1 ? 's' : ''}</span>
                    {subject.absences > 0 && (
                      <span>{subject.absences} absence{subject.absences > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              </button>
              
              {selectedSubject === subject.id && (
                <div className="mt-4">
                  {/* Subject Appreciations */}
                  <div className="mb-4">
                    <h3 className="text-lg font-medium mb-2">Appréciations</h3>
                    <div className="space-y-4">
                      {appreciations
                        .filter(a => a.subject_id === subject.id)
                        .map(appreciation => (
                          <div key={appreciation.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="text-sm text-gray-500">
                                  Du {formatDate(appreciation.start_date)} au {formatDate(appreciation.end_date)}
                                </p>
                              </div>
                            </div>
                            <p className="text-gray-700">{appreciation.content}</p>
                            <p className="text-sm text-gray-500 mt-2">
                              Par {appreciation.author?.prenom} {appreciation.author?.nom}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                  
                  {/* Subject Grades */}
                  <h3 className="text-lg font-medium mb-2">Notes</h3>
                  <div className="space-y-2">
                  {subjectGrades.map(grade => (
                    <div key={grade.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">
                            {grade.exam_title || getExamTypeText(grade.exam_type)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(grade.exam_date)}
                          </div>
                        </div>
                        <div className="text-right">
                          {grade.is_absent ? (
                            <span className="font-medium text-gray-500">ABS</span>
                          ) : (
                            <span className={`font-medium ${
                              (grade.grade || 0) >= 10 ? 'text-success-500' : 'text-error-500'
                            }`}>
                              {grade.grade}/20
                            </span>
                          )}
                          {grade.coefficient !== 1 && (
                            <div className="text-xs text-gray-500">
                              Coef. {grade.coefficient}
                            </div>
                          )}
                        </div>
                      </div>
                      {grade.comment && (
                        <p className="mt-2 text-sm text-gray-600 border-t border-gray-200 pt-2">
                          {grade.comment}
                        </p>
                      )}
                    </div>
                  ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* General Appreciations */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Appréciations générales</h2>
        <div className="space-y-4">
          {appreciations
            .filter(a => !a.subject_id)
            .map(appreciation => (
              <div key={appreciation.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-sm text-gray-500">
                      Du {formatDate(appreciation.start_date)} au {formatDate(appreciation.end_date)}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700">{appreciation.content}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Par {appreciation.author?.prenom} {appreciation.author?.nom}
                </p>
              </div>
            ))}
        </div>
      </div>

      {/* Appreciations Section */}
      
      {/* Subject Averages */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Enseignement général</h2>
        <div className="space-y-4">
          {subjectAverages
            .filter(subject => subject.teaching_type === 'enseignement_general')
            .map((subject) => (
            <div key={subject.id}>
              <button
                onClick={() => handleSubjectClick(subject.id)}
                className="w-full bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 p-4"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">{subject.name}</h3>
                    {subject.className && (
                      <span className="text-sm text-gray-500">{subject.className}</span>
                    )}
                  </div>
                  <div className="flex items-center">
                    <span className={`font-semibold text-lg ${
                      subject.average >= 10 ? 'text-success-500' : 'text-error-500'
                    }`}>{subject.count > 0 ? `${subject.average.toFixed(2)}/20` : '-'}</span>
                    <ChevronRight className={`h-5 w-5 ml-2 transition-transform ${
                      selectedSubject === subject.id ? 'rotate-90' : ''
                    }`} />
                  </div>
                </div>
                
                <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                  <span>Coefficient {subject.coefficient}</span>
                  <div className="flex items-center space-x-4">
                    <span>{subject.count} note{subject.count !== 1 ? 's' : ''}</span>
                    {subject.absences > 0 && (
                      <span>{subject.absences} absence{subject.absences > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              </button>
              
              {/* Subject Appreciations */}
              {/* Subject Grades */}
              {selectedSubject === subject.id && (
                <div className="mt-4">
                  {/* Subject Appreciations */}
                  <div className="mb-4">
                    <h3 className="text-lg font-medium mb-2">Appréciations</h3>
                    <div className="space-y-4">
                      {appreciations
                        .filter(a => a.subject_id === subject.id)
                        .map(appreciation => (
                          <div key={appreciation.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="text-sm text-gray-500">
                                  Du {formatDate(appreciation.start_date)} au {formatDate(appreciation.end_date)}
                                </p>
                              </div>
                            </div>
                            <p className="text-gray-700">{appreciation.content}</p>
                            <p className="text-sm text-gray-500 mt-2">
                              Par {appreciation.author?.prenom} {appreciation.author?.nom}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                  
                  {/* Subject Grades */}
                  <h3 className="text-lg font-medium mb-2">Notes</h3>
                  <div className="space-y-2">
                  {subjectGrades.map(grade => (
                    <div key={grade.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">
                            {grade.exam_title || getExamTypeText(grade.exam_type)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDate(grade.exam_date)}
                          </div>
                        </div>
                        <div className="text-right">
                          {grade.is_absent ? (
                            <span className="font-medium text-gray-500">ABS</span>
                          ) : (
                            <span className={`font-medium ${
                              (grade.grade || 0) >= 10 ? 'text-success-500' : 'text-error-500'
                            }`}>
                              {grade.grade}/20
                            </span>
                          )}
                          {grade.coefficient !== 1 && (
                            <div className="text-xs text-gray-500">
                              Coef. {grade.coefficient}
                            </div>
                          )}
                        </div>
                      </div>
                      {grade.comment && (
                        <p className="mt-2 text-sm text-gray-600 border-t border-gray-200 pt-2">
                          {grade.comment}
                        </p>
                      )}
                    </div>
                  ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="animate-fade-in">
      <Routes>
        <Route index element={<DashboardHome />} />
        <Route path="grades" element={<StudentGrades />} />
        <Route path="profile" element={<StudentProfile />} />
        <Route path="livret" element={<StudentLivret />} />
      </Routes>
    </div>
  );
};

export default StudentDashboard;